import React, { useState, useEffect } from 'react';
import styles from './style.module.scss';
import { GET, POST, PUT, DELETE } from '../../../API';
import Button from '../../../comps/button';
import Loading from '../../../comps/loading';
import Input from '../../../comps/input';
import { GoSearch } from 'react-icons/go';
import { FiEdit2, FiTrash2, FiPlus, FiX, FiCheck } from 'react-icons/fi';

// Art categories for the foundation site
const ART_CATEGORIES = [
  { id: 'art', name: 'אמנות חזותית' },
  { id: 'dance', name: 'מחול' },
  { id: 'literature', name: 'ספרות' },
  { id: 'music', name: 'מוסיקה' },
  { id: 'print', name: 'הוצאה לאור' },
  { id: 'matan', name: 'מתן' },
];

// The "print" (book publishing) category stores books grouped by subtype
// instead of the standard name/project/amount winner shape.
const PRINT_CATEGORY = 'print';
const PRINT_SUBTYPES = [
  { id: 'mofet', name: 'ספרות מופת' },
  { id: 'bihurim', name: 'ספרי ביכורים' },
  { id: 'iun', name: 'ספרי עיון' },
  { id: 'shira', name: 'ספרי שירה' },
  { id: 'jewish', name: 'ספרות יהודית' },
];
const subtypeLabel = (id) =>
  PRINT_SUBTYPES.find((s) => s.id === id)?.name || id;

const EMPTY_WINNER = { name: '', project: '', amount: 0 };
const EMPTY_BOOK = {
  subtype: 'mofet',
  name: '',
  publisher: '',
  author: '',
  lang: '',
  translator: '',
};

// Generate years from 2012 to current year + 1
const generateYears = () => {
  const currentYear = new Date().getFullYear();
  const years = [];
  for (let year = currentYear + 1; year >= 2012; year--) {
    years.push(year);
  }
  return years;
};

const YEARS = generateYears();

export default function Winners() {
  const [isLoading, setIsLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('art');
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [search, setSearch] = useState('');
  const [editingIndex, setEditingIndex] = useState(null);
  const [editForm, setEditForm] = useState(EMPTY_WINNER);
  const [isAdding, setIsAdding] = useState(false);
  const [newRow, setNewRow] = useState(EMPTY_WINNER);

  const isPrint = selectedCategory === PRINT_CATEGORY;

  const fetchRows = async () => {
    setIsLoading(true);
    try {
      const res = await GET(
        `winners/list?category=${selectedCategory}&year=${selectedYear}`
      );
      if (res.ok) {
        const data = await res.json();
        setRows(
          selectedCategory === PRINT_CATEGORY
            ? data.books || []
            : data.winners || []
        );
      } else {
        setRows([]);
      }
    } catch (error) {
      console.error('Error fetching winners:', error);
      setRows([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    // Reset transient edit/add state whenever the list context changes
    setEditingIndex(null);
    setIsAdding(false);
    setSearch('');
    setEditForm(selectedCategory === PRINT_CATEGORY ? EMPTY_BOOK : EMPTY_WINNER);
    setNewRow(selectedCategory === PRINT_CATEGORY ? EMPTY_BOOK : EMPTY_WINNER);
    fetchRows();
  }, [selectedCategory, selectedYear]);

  const matchesSearch = (row) => {
    const q = search.toLowerCase();
    if (!q) return true;
    if (isPrint) {
      return (
        (row.name || '').toLowerCase().includes(q) ||
        (row.author || '').toLowerCase().includes(q) ||
        (row.publisher || '').toLowerCase().includes(q) ||
        (row.translator || '').toLowerCase().includes(q)
      );
    }
    return (
      (row.name || '').toLowerCase().includes(q) ||
      (row.project || '').toLowerCase().includes(q)
    );
  };

  const filteredRows = rows
    .map((row, index) => ({ ...row, _index: index }))
    .filter(matchesSearch);

  // Persist the given list of rows for the current category/year.
  const saveRows = async (updatedRows) => {
    const body = isPrint
      ? { category: selectedCategory, year: selectedYear, books: updatedRows }
      : { category: selectedCategory, year: selectedYear, winners: updatedRows };
    return POST('winners/update', body);
  };

  const handleEdit = (row) => {
    setEditingIndex(row._index);
    if (isPrint) {
      setEditForm({
        subtype: row.subtype || 'mofet',
        name: row.name || '',
        publisher: row.publisher || '',
        author: row.author || '',
        lang: row.lang || '',
        translator: row.translator || '',
      });
    } else {
      setEditForm({
        name: row.name,
        project: row.project,
        amount: row.amount,
      });
    }
  };

  const handleCancelEdit = () => {
    setEditingIndex(null);
    setEditForm(isPrint ? EMPTY_BOOK : EMPTY_WINNER);
  };

  const handleSaveEdit = async (index) => {
    if (isPrint && !editForm.name) {
      alert('יש למלא כותר');
      return;
    }
    try {
      const updatedRows = rows.map((r, i) =>
        i === index
          ? isPrint
            ? {
                subtype: editForm.subtype,
                name: editForm.name,
                publisher: editForm.publisher,
                author: editForm.author,
                lang: editForm.lang,
                translator: editForm.translator,
              }
            : {
                name: editForm.name,
                project: editForm.project,
                amount: editForm.amount,
              }
          : r
      );

      const res = await saveRows(updatedRows);

      if (res.ok) {
        setRows(updatedRows);
        handleCancelEdit();
      } else {
        alert('שגיאה בעדכון');
      }
    } catch (error) {
      console.error('Error updating row:', error);
      alert('שגיאה בעדכון');
    }
  };

  const handleDelete = async (index) => {
    if (!window.confirm('האם אתה בטוח שברצונך למחוק רשומה זו?')) {
      return;
    }
    try {
      const updatedRows = rows.filter((_, i) => i !== index);

      const res = await saveRows(updatedRows);

      if (res.ok) {
        setRows(updatedRows);
      } else {
        alert('שגיאה במחיקה');
      }
    } catch (error) {
      console.error('Error deleting row:', error);
      alert('שגיאה במחיקה');
    }
  };

  const handleAdd = async () => {
    if (isPrint) {
      if (!newRow.name) {
        alert('יש למלא כותר');
        return;
      }
    } else if (!newRow.name || !newRow.project) {
      alert('יש למלא שם ופרויקט');
      return;
    }
    try {
      const entry = isPrint
        ? {
            subtype: newRow.subtype,
            name: newRow.name,
            publisher: newRow.publisher,
            author: newRow.author,
            lang: newRow.lang,
            translator: newRow.translator,
          }
        : {
            name: newRow.name,
            project: newRow.project,
            amount: newRow.amount,
          };
      const updatedRows = [...rows, entry];

      const res = await saveRows(updatedRows);

      if (res.ok) {
        setRows(updatedRows);
        setNewRow(isPrint ? EMPTY_BOOK : EMPTY_WINNER);
        setIsAdding(false);
      } else {
        alert('שגיאה בהוספה');
      }
    } catch (error) {
      console.error('Error adding row:', error);
      alert('שגיאה בהוספה');
    }
  };

  const updateNew = (field, value) =>
    setNewRow((prev) => ({ ...prev, [field]: value }));
  const updateEdit = (field, value) =>
    setEditForm((prev) => ({ ...prev, [field]: value }));

  return (
    <div className={styles.Container}>
      <h1>ניהול זוכים - אתר האמנויות</h1>

      <div className={styles.Filters}>
        <div className={styles.FilterGroup}>
          <label>תחום:</label>
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
          >
            {ART_CATEGORIES.map((cat) => (
              <option key={cat.id} value={cat.id}>
                {cat.name}
              </option>
            ))}
          </select>
        </div>

        <div className={styles.FilterGroup}>
          <label>שנה:</label>
          <select
            value={selectedYear}
            onChange={(e) => setSelectedYear(Number(e.target.value))}
          >
            {YEARS.map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </select>
        </div>

        <div className={styles.SearchBar}>
          <Input
            placeholder={isPrint ? 'חיפוש ספרים...' : 'חיפוש זוכים...'}
            icon={<GoSearch />}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      <div className={styles.Actions}>
        <Button onClick={() => setIsAdding(true)} disabled={isAdding}>
          <FiPlus /> {isPrint ? 'הוסף ספר חדש' : 'הוסף זוכה חדש'}
        </Button>
      </div>

      {isAdding && (
        <div className={styles.AddForm}>
          <h3>{isPrint ? 'הוספת ספר חדש' : 'הוספת זוכה חדש'}</h3>
          {isPrint ? (
            <div className={styles.FormRow}>
              <select
                value={newRow.subtype}
                onChange={(e) => updateNew('subtype', e.target.value)}
              >
                {PRINT_SUBTYPES.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
              <Input
                placeholder="כותר"
                value={newRow.name}
                onChange={(e) => updateNew('name', e.target.value)}
              />
              <Input
                placeholder='מו"ל'
                value={newRow.publisher}
                onChange={(e) => updateNew('publisher', e.target.value)}
              />
              <Input
                placeholder="מחבר.ת"
                value={newRow.author}
                onChange={(e) => updateNew('author', e.target.value)}
              />
              <Input
                placeholder="שפת מקור"
                value={newRow.lang}
                onChange={(e) => updateNew('lang', e.target.value)}
              />
              <Input
                placeholder="מתרגם.ת"
                value={newRow.translator}
                onChange={(e) => updateNew('translator', e.target.value)}
              />
            </div>
          ) : (
            <div className={styles.FormRow}>
              <Input
                placeholder="שם"
                value={newRow.name}
                onChange={(e) => updateNew('name', e.target.value)}
              />
              <Input
                placeholder="פרויקט"
                value={newRow.project}
                onChange={(e) => updateNew('project', e.target.value)}
              />
              <Input
                placeholder="סכום"
                type="number"
                value={newRow.amount}
                onChange={(e) => updateNew('amount', Number(e.target.value))}
              />
            </div>
          )}
          <div className={styles.FormActions}>
            <Button onClick={handleAdd}>
              <FiCheck /> שמור
            </Button>
            <Button
              onClick={() => {
                setIsAdding(false);
                setNewRow(isPrint ? EMPTY_BOOK : EMPTY_WINNER);
              }}
            >
              <FiX /> ביטול
            </Button>
          </div>
        </div>
      )}

      {isLoading ? (
        <div className={styles.LoadingWrapper}>
          <Loading />
        </div>
      ) : (
        <div className={styles.TableWrapper}>
          <table className={styles.Table}>
            <thead>
              {isPrint ? (
                <tr>
                  <th>קטגוריה</th>
                  <th>כותר</th>
                  <th>מו"ל</th>
                  <th>מחבר.ת</th>
                  <th>שפת מקור</th>
                  <th>מתרגם.ת</th>
                  <th>פעולות</th>
                </tr>
              ) : (
                <tr>
                  <th>שם</th>
                  <th>פרויקט</th>
                  <th>סכום</th>
                  <th>פעולות</th>
                </tr>
              )}
            </thead>
            <tbody>
              {filteredRows.length === 0 ? (
                <tr>
                  <td colSpan={isPrint ? 7 : 4} className={styles.Empty}>
                    {search
                      ? 'לא נמצאו תוצאות'
                      : isPrint
                      ? 'אין ספרים לתצוגה'
                      : 'אין זוכים לתצוגה'}
                  </td>
                </tr>
              ) : (
                filteredRows.map((row) => {
                  const isEditing = editingIndex === row._index;

                  if (isPrint) {
                    return (
                      <tr key={row._index}>
                        <td>
                          {isEditing ? (
                            <select
                              value={editForm.subtype}
                              onChange={(e) =>
                                updateEdit('subtype', e.target.value)
                              }
                            >
                              {PRINT_SUBTYPES.map((s) => (
                                <option key={s.id} value={s.id}>
                                  {s.name}
                                </option>
                              ))}
                            </select>
                          ) : (
                            subtypeLabel(row.subtype)
                          )}
                        </td>
                        <td>
                          {isEditing ? (
                            <Input
                              value={editForm.name}
                              onChange={(e) => updateEdit('name', e.target.value)}
                            />
                          ) : (
                            row.name
                          )}
                        </td>
                        <td>
                          {isEditing ? (
                            <Input
                              value={editForm.publisher}
                              onChange={(e) =>
                                updateEdit('publisher', e.target.value)
                              }
                            />
                          ) : (
                            row.publisher
                          )}
                        </td>
                        <td>
                          {isEditing ? (
                            <Input
                              value={editForm.author}
                              onChange={(e) =>
                                updateEdit('author', e.target.value)
                              }
                            />
                          ) : (
                            row.author
                          )}
                        </td>
                        <td>
                          {isEditing ? (
                            <Input
                              value={editForm.lang}
                              onChange={(e) => updateEdit('lang', e.target.value)}
                            />
                          ) : (
                            row.lang
                          )}
                        </td>
                        <td>
                          {isEditing ? (
                            <Input
                              value={editForm.translator}
                              onChange={(e) =>
                                updateEdit('translator', e.target.value)
                              }
                            />
                          ) : (
                            row.translator
                          )}
                        </td>
                        <td className={styles.ActionsCell}>
                          {isEditing ? (
                            <>
                              <button
                                className={styles.IconBtn}
                                onClick={() => handleSaveEdit(row._index)}
                                title="שמור"
                              >
                                <FiCheck />
                              </button>
                              <button
                                className={styles.IconBtn}
                                onClick={handleCancelEdit}
                                title="ביטול"
                              >
                                <FiX />
                              </button>
                            </>
                          ) : (
                            <>
                              <button
                                className={styles.IconBtn}
                                onClick={() => handleEdit(row)}
                                title="עריכה"
                              >
                                <FiEdit2 />
                              </button>
                              <button
                                className={styles.IconBtn}
                                onClick={() => handleDelete(row._index)}
                                title="מחיקה"
                              >
                                <FiTrash2 />
                              </button>
                            </>
                          )}
                        </td>
                      </tr>
                    );
                  }

                  return (
                    <tr key={row._index}>
                      <td>
                        {isEditing ? (
                          <Input
                            value={editForm.name}
                            onChange={(e) => updateEdit('name', e.target.value)}
                          />
                        ) : (
                          row.name
                        )}
                      </td>
                      <td>
                        {isEditing ? (
                          <Input
                            value={editForm.project}
                            onChange={(e) =>
                              updateEdit('project', e.target.value)
                            }
                          />
                        ) : (
                          row.project
                        )}
                      </td>
                      <td>
                        {isEditing ? (
                          <Input
                            type="number"
                            value={editForm.amount}
                            onChange={(e) =>
                              updateEdit('amount', Number(e.target.value))
                            }
                          />
                        ) : (
                          `${row.amount?.toLocaleString() || 0} ₪`
                        )}
                      </td>
                      <td className={styles.ActionsCell}>
                        {isEditing ? (
                          <>
                            <button
                              className={styles.IconBtn}
                              onClick={() => handleSaveEdit(row._index)}
                              title="שמור"
                            >
                              <FiCheck />
                            </button>
                            <button
                              className={styles.IconBtn}
                              onClick={handleCancelEdit}
                              title="ביטול"
                            >
                              <FiX />
                            </button>
                          </>
                        ) : (
                          <>
                            <button
                              className={styles.IconBtn}
                              onClick={() => handleEdit(row)}
                              title="עריכה"
                            >
                              <FiEdit2 />
                            </button>
                            <button
                              className={styles.IconBtn}
                              onClick={() => handleDelete(row._index)}
                              title="מחיקה"
                            >
                              <FiTrash2 />
                            </button>
                          </>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      )}

      <div className={styles.Summary}>
        <span>
          {isPrint
            ? `סה"כ ספרים: ${filteredRows.length}`
            : `סה"כ זוכים: ${filteredRows.length}`}
        </span>
        {!isPrint && (
          <span>
            סה"כ סכום:{' '}
            {filteredRows
              .reduce((sum, w) => sum + (w.amount || 0), 0)
              .toLocaleString()}{' '}
            ₪
          </span>
        )}
      </div>
    </div>
  );
}
